// Boot splash intro — orchestrates dismissal of the #boot-splash overlay that
// index.html paints on first render (see index.html). The overlay animates the
// brand mark (pallet-jack draws in → "stopal" wordmark assembles) purely in
// CSS so it appears instantly on every cold load, native shell or web, with no
// dependency on React mount timing.
//
// This module only decides WHEN to fade it out: not before the animation has
// been seen (BOOT_SPLASH_MIN_VISIBLE_MS) AND not before the app is ready to render behind
// it — whichever finishes last. Playwright / reduced-motion callers dismiss
// immediately so e2e and motion-sensitive users are never blocked.

export const BOOT_SPLASH_MIN_VISIBLE_MS = 3600 // type-in → hold → pull ('sto' out, 'pal!' settles)
const FADE_MS = 500
const REMOVE_FALLBACK_MS = FADE_MS + 200

function removeOverlay(el: HTMLElement): void {
  el.remove()
}

function fadeOut(el: HTMLElement): void {
  el.classList.add('is-done')
  el.addEventListener('transitionend', () => removeOverlay(el), { once: true })
  // Bounded fallback: never leave the overlay behind if transitionend is
  // missed (element already opacity:0, animation interrupted, etc.).
  window.setTimeout(() => removeOverlay(el), REMOVE_FALLBACK_MS)
}

/**
 * Fade the boot splash once the app is ready AND the intro has been on screen
 * for at least BOOT_SPLASH_MIN_VISIBLE_MS. Safe to call when the overlay is absent (no-op).
 *
 * @param appReady resolves when the app has painted its first content.
 */
export function dismissBootSplash(appReady: Promise<unknown>): void {
  const el = document.getElementById('boot-splash')
  if (!el) return

  const reduceMotion = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  ).matches
  const automated = navigator.webdriver === true
  if (reduceMotion || automated) {
    // Skip the timed intro: dismiss as soon as the app is ready.
    void Promise.resolve(appReady).then(() => fadeOut(el))
    return
  }

  void Promise.resolve(appReady).then(() => {
    // performance.now() is navigation-relative, so time spent fetching and
    // evaluating the entry bundle counts toward the intro instead of starting
    // a second full wait once the bundle finally executes.
    const remaining = Math.max(
      0,
      BOOT_SPLASH_MIN_VISIBLE_MS - performance.now(),
    )
    window.setTimeout(() => fadeOut(el), remaining)
  })
}
